import { and, eq, gt, lt } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo } from 'react'
import { secondsToHours, sumNetWorkSeconds } from '@/lib/dates'
import { useStore } from '@/lib/pocketbase'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

interface UseApprovedJobEntriesOptions {
    jobId: string | undefined
    enabled?: boolean
    startDate?: string
    endDate?: string
}

export function useApprovedJobEntries({
    jobId,
    enabled = true,
    startDate,
    endDate,
}: UseApprovedJobEntriesOptions) {
    const [timeEntriesCollection, usersCollection, locationsCollection] = useStore(
        'time_entries',
        'users',
        'locations'
    )

    const { isLoading, data: rawEntries = [] } = useLiveQuery(
        q =>
            jobId && enabled
                ? q
                      .from({ entries: timeEntriesCollection })
                      .join(
                          { user: usersCollection },
                          ({ entries, user }) => eq(entries.user, user.id),
                          'left'
                      )
                      .join(
                          { location: locationsCollection },
                          ({ entries, location }) => eq(entries.location, location.id),
                          'left'
                      )
                      .where(({ entries }) => {
                          let condition = and(
                              eq(entries.job, jobId),
                              eq(entries.status, 'approved')
                          )
                          if (startDate) condition = and(condition, gt(entries.clock_in, startDate))
                          if (endDate) condition = and(condition, lt(entries.clock_in, endDate))
                          return condition
                      })
                      .orderBy(({ entries }) => entries.clock_in, 'desc')
                : undefined,
        [jobId, enabled, startDate, endDate]
    )

    const entries = useMemo(() => {
        return rawEntries.map(row => {
            const entry = row.entries as TimeEntriesExpanded
            return {
                ...entry,
                expand: {
                    ...entry.expand,
                    user: row.user ?? entry.expand?.user,
                    location: row.location ?? entry.expand?.location,
                },
            }
        })
    }, [rawEntries])

    const totalHours = secondsToHours(sumNetWorkSeconds(entries))

    return {
        entries,
        totalHours,
        isLoading,
    }
}
